import React, { useState } from 'react'
import { Box, Button, Chip, IconButton, Typography } from '@mui/material'
import { Close } from '@mui/icons-material'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { BULK_RADIUS_PRESETS } from './radius'

type LocationsSelectionBarProps = {
  selectedCount: number
  /** Shown once the whole page is selected and more locations match */
  selectAllCount?: number
  canSetRadius: boolean
  onSelectAll: () => void
  onSetRadius: (radius: number) => void
  onRemoveSelected: () => void
  onDeselect: () => void
}

/** Replaces the summary while locations are selected */
const LocationsSelectionBar: React.FC<LocationsSelectionBarProps> = ({
  selectedCount,
  selectAllCount,
  canSetRadius,
  onSelectAll,
  onSetRadius,
  onRemoveSelected,
  onDeselect,
}) => {
  const t = useAppTranslations()
  const [radiusPresetsOpen, setRadiusPresetsOpen] = useState(false)

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'action.selected' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, minHeight: 40, pl: 1.5, pr: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {t('adgroup.geoTargeting.panel.selected', { count: selectedCount })}
        </Typography>
        {selectAllCount !== undefined && (
          <Button size="small" onClick={onSelectAll}>
            {t('adgroup.geoTargeting.panel.selectAllMatching', { count: selectAllCount })}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {canSetRadius && (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            aria-expanded={radiusPresetsOpen}
            onClick={() => setRadiusPresetsOpen((open) => !open)}
          >
            {t('adgroup.geoTargeting.panel.setRadius')}
          </Button>
        )}
        <Button size="small" variant="outlined" color="error" onClick={onRemoveSelected}>
          {t('adgroup.geoTargeting.panel.removeSelected')}
        </Button>
        <IconButton size="small" onClick={onDeselect} aria-label={t('adgroup.geoTargeting.panel.deselectAll')}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      {canSetRadius && radiusPresetsOpen && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, px: 1.5, pb: 1 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mr: 0.5 }}>
            {t('adgroup.geoTargeting.panel.radiusFor', { count: selectedCount })}
          </Typography>
          {BULK_RADIUS_PRESETS.map((radius) => (
            <Chip
              key={radius}
              size="small"
              label={t('adgroup.geoTargeting.panel.radiusValue', { radius })}
              color="primary"
              variant="outlined"
              clickable
              onClick={() => {
                onSetRadius(radius)
                setRadiusPresetsOpen(false)
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default LocationsSelectionBar
